import { z } from 'zod';

export const DeploymentStatusSchema = z.enum([
  'pending',
  'compiling',
  'validating',
  'transferring',
  'verifying',
  'running',
  'failed',
  'rolled_back',
]);

// =====================================================================
// Plan 9 — connection_mode tells cloud-api how to reach the target
// for discovery + deploy. All targets created before Plan 9 lack these
// fields, so they're nullish (undefined or null both parse).
// Spec §4.1.
// =====================================================================
export const ConnectionModeSchema = z.enum([
  'cloud_api_via_pi', // editor → cloud-api → Pi (scenario 3)
  'pi_lan_direct', // editor → Pi LAN (scenario 2 fast path)
  'board_lan_direct', // editor → board LAN (scenario 1)
  'cloud_api_stub', // cloud-api returns mocked discovery (no Pi yet)
]);

export const DeploymentTargetSchema = z.object({
  id: z.string().min(1),
  project_id: z.string().min(1),
  label: z.string().min(1),
  master_board_id: z.string().min(1),
  last_deployed_version: z.string().nullable(),
  last_deployed_at_ms: z.number().int().nonnegative().nullable(),
  // Plan 9 additions — nullish so existing target docs still parse.
  connection_mode: ConnectionModeSchema.nullish(),
  discovery_address: z.string().nullish(),
  discovery_token: z.string().nullish(),
});

export const DeploymentSchema = z.object({
  id: z.string().min(1),
  project_id: z.string().min(1),
  target_id: z.string().min(1),
  program_id: z.string().min(1),
  version: z.string().min(1),
  status: DeploymentStatusSchema,
  started_at_ms: z.number().int().nonnegative(),
  completed_at_ms: z.number().int().nonnegative().nullable(),
  error_message: z.string().nullable(),
});

export const DeployProgressEventSchema = z.object({
  deployment_id: z.string().min(1),
  phase: z.string().min(1),
  progress_pct: z.number().int().min(0).max(100),
  message: z.string().nullable(),
});

// =====================================================================
// Task 7 — a project can pin a specific discovered master board for
// deploy targeting. `board_id` identifies the master board; `can_id` is
// its bus address (0-255). The deploy route resolves the actual deploy
// target from this binding unless the request overrides it (spec §7).
// =====================================================================
export const MasterBindingSchema = z.object({
  board_id: z.string(),
  can_id: z.number().int().min(0).max(255),
});

export type ParsedConnectionMode = z.infer<typeof ConnectionModeSchema>;
export type ParsedDeployment = z.infer<typeof DeploymentSchema>;
export type ParsedDeploymentTarget = z.infer<typeof DeploymentTargetSchema>;
export type ParsedDeployProgressEvent = z.infer<typeof DeployProgressEventSchema>;
export type MasterBinding = z.infer<typeof MasterBindingSchema>;
